import bigDecimal from 'js-big-decimal'

import {
  GenericPriceInterface,
  ProductInterface,
} from '../interfaces/ServerInterfaces'
import { useAppSelector } from '../store/hooks'

// ProductInterface has been updated to use proper typing for isInternetVisible

const useProduct = () => {
  const { business } = useAppSelector((state) => state.init)
  const { availableCurrencies, mainCurrency, costCurrency, configurationsKey } =
    business ?? {}
  const precision = Number(
    configurationsKey?.find((item) => item.key === 'precission_after_coma')
      ?.value ?? '2'
  )

  const formatPrice = (
    price: GenericPriceInterface,
    setPrecision?: boolean
  ): string => {
    const newPrice = new bigDecimal(price?.amount ?? price.price ?? 0)
    const currency = availableCurrencies?.find(
      (currency) => currency.code === price.codeCurrency
    )

    let toReturn = newPrice.round(setPrecision ? precision : 2).getPrettyValue()

    if (setPrecision) {
      const patron = /^([0-9]?[,0-9]+\.\d{2})([0-9]*[1-9]+)*(0+)$/g
      toReturn = toReturn.replace(patron, '$1$2')
    }

    return `${toReturn} ${currency ? price.codeCurrency : mainCurrency}`
  }

  const convertCurrency = (
    price: GenericPriceInterface,
    codeCurrency: string
  ): GenericPriceInterface => {
    if (price.codeCurrency === codeCurrency) return price

    const targetCurrency = availableCurrencies?.find(
      (curr) => curr.code === codeCurrency
    )

    const key: keyof GenericPriceInterface =
      price.price !== undefined ? 'price' : 'amount'

    //verificar que la moneda especificada existe y esta configurada en el negocio
    if (!targetCurrency) {
      return price
    }

    const priceInMainCurrency: GenericPriceInterface = { ...price }
    //Get price in mainCurrency
    if (price.codeCurrency !== mainCurrency) {
      const priceAmount = new bigDecimal(price.price || price.amount)
      const currency = availableCurrencies?.find(
        (curr) => curr.code === price.codeCurrency
      )
      const rate = new bigDecimal(currency?.exchangeRate)
      const amount = priceAmount.multiply(rate).getValue()
      priceInMainCurrency[key] = Number(amount)
      priceInMainCurrency.codeCurrency = mainCurrency ?? ''
    }

    if (codeCurrency === mainCurrency) {
      return {
        [key]: priceInMainCurrency[key],
        codeCurrency: priceInMainCurrency.codeCurrency,
      }
    }

    const targetPrice = Number(
      bigDecimal.divide(priceInMainCurrency[key], targetCurrency?.exchangeRate)
    )

    return { [key]: targetPrice, codeCurrency }
  }

  const getMainPrice = (product: ProductInterface, priceSystemId?: number) => {
    let mainPrice = {
      ...product.prices.find((price) => price.isMain),
    }

    if (priceSystemId) {
      const idx = product.prices.findIndex(
        (price) => price.priceSystemId === priceSystemId
      )

      if (idx !== -1) {
        mainPrice = { ...product.prices[idx] }
      }
    }

    if (product?.onSale) {
      if (product.onSaleType === 'percent') {
        const discountAmount = bigDecimal.multiply(
          mainPrice.price,
          product.onSaleDiscountAmount * 0.01
        )
        const priceAmount = bigDecimal.subtract(mainPrice.price, discountAmount)
        mainPrice.price = Number(priceAmount)
      } else if (
        product.onSaleType === 'fixed' ||
        !!product?.onSalePrice?.amount
      ) {
        mainPrice = {
          ...mainPrice,
          price: product.onSalePrice.amount,
          codeCurrency: product.onSalePrice.codeCurrency,
        }
      }
    }

    return mainPrice
  }

  const getCost = ({
    product,
    mode = 'price',
    setPrecision,
  }: {
    product: ProductInterface
    mode?: 'price' | 'number'
    setPrecision?: boolean
  }) => {
    const { type, averageCost, supplies, fixedCosts } = product

    let cost = new bigDecimal(averageCost)

    if (
      ['MANUFACTURED', 'MENU', 'STOCK', 'ADDON'].includes(type) &&
      (supplies?.length !== 0 || fixedCosts?.length !== 0)
    ) {
      /* const suppliesCost = new bigDecimal(
        supplies?.reduce(
          (total, value) => total + value.quantity * value.supply?.averageCost,
          0
        )
      )

      const fixedCost = new bigDecimal(
        fixedCosts?.reduce(
          (total, item) =>
            total +
            (item.type === 'percent'
              ? (item.costAmount / 100) * (product?.netCost ?? 0)
              : item.costAmount),
          0
        )
      ) */

      cost = new bigDecimal(product.averageCost)
    }

    const toReturn = Number(cost.round(setPrecision ? precision : 2).getValue())

    return mode === 'number'
      ? toReturn
      : formatPrice({ price: toReturn, codeCurrency: costCurrency ?? '' }, true)
  }

  const getPrice = ({
    product,
    codeCurrency,
    priceSystemId,
    returnAsPrice,
  }: {
    product: ProductInterface
    codeCurrency?: string
    priceSystemId?: number
    returnAsPrice?: boolean
  }) => {
    const mainPrice = getMainPrice(product, priceSystemId)

    if (!codeCurrency || mainPrice.codeCurrency === codeCurrency)
      return returnAsPrice
        ? { price: mainPrice.price, codeCurrency: mainPrice.codeCurrency }
        : formatPrice(mainPrice as GenericPriceInterface)

    const targetPrice = convertCurrency(
      mainPrice as GenericPriceInterface,
      codeCurrency
    )

    return returnAsPrice
      ? targetPrice
      : formatPrice({
          price: targetPrice.price,
          codeCurrency: targetPrice.codeCurrency,
        })
  }

  const getProfit = ({
    product,
    codeCurrency,
    priceSystemId,
    mode = 'number',
    ref = 'sale',
  }: {
    product: ProductInterface
    codeCurrency?: string
    priceSystemId?: number
    mode?: 'percent' | 'number' | 'price'
    ref?: 'cost' | 'sale'
  }) => {
    if (!costCurrency) {
      // eslint-disable-next-line no-console
      console.error('Verifique que este configurada la moneda de costo')
      return 0
    }

    const cost = new bigDecimal(getCost({ product, mode: 'number' }) as number)
    const priceToCostCurrency = getPrice({
      product,
      codeCurrency: costCurrency,
      priceSystemId,
      returnAsPrice: true,
    })

    const price = new bigDecimal(
      (priceToCostCurrency as GenericPriceInterface).price
    )
    let profit = price.subtract(cost)
    let currency = costCurrency

    if (codeCurrency && codeCurrency !== costCurrency) {
      const currentProfitValue = Number(profit.getValue())
      const profitToCurrency = convertCurrency(
        {
          price: currentProfitValue,
          codeCurrency: costCurrency,
        },
        codeCurrency
      ).price

      profit = new bigDecimal(profitToCurrency)
      currency = codeCurrency
    }

    switch (mode) {
      case 'percent':
        if (price.compareTo(new bigDecimal(0)) === 0 && ref === 'sale')
          return -100
        if (cost.compareTo(new bigDecimal(0)) === 0 && ref === 'cost')
          return 100
        return ref === 'sale'
          ? Number(
              profit
                .divide(price)
                .round(4)
                .multiply(new bigDecimal(100))
                .getValue()
            )
          : Number(
              price
                .divide(cost)
                .subtract(new bigDecimal(1))
                .round(4)
                .multiply(new bigDecimal(100))
                .getValue()
            )
      case 'price':
        return `${profit?.round(2).getPrettyValue()} ${currency}`
      default:
        return Number(profit.round(2).getValue())
    }
  }

  return {
    convertCurrency,
    formatPrice,
    getCost,
    getPrice,
    getProfit,
  }
}

export default useProduct
